import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Camera, Star, TrendingUp, Calendar, Users, Award } from 'lucide-react';
import { toast } from 'sonner';
import ProfilePictureModal from '../components/ProfilePictureModal';
import AccountSettingsModal from '../components/AccountSettingsModal';

export default function Profile({ user, onUpdate }) {
  const [stats, setStats] = useState({
    ridesCreated: 0,
    ridesJoined: 0,
    ridesCompleted: 0,
    cyclingBuddies: []
  });
  const [recentRides, setRecentRides] = useState({ created: [], joined: [] });
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchStats();
    fetchRecentRides();
  }, []);

  const fetchStats = async () => {
    try {
      const [created, joined] = await Promise.all([
        axios.get('/rides/my-rides'),
        axios.get('/rides/joined')
      ]);
      
      const completed = [...created.data, ...joined.data].filter(r => r.status === 'completed').length;
      
      // Get unique cycling buddies from joined rides
      const buddies = new Set();
      joined.data.forEach(ride => {
        if (ride.creator?.id !== user.id) {
          buddies.add(JSON.stringify({
            id: ride.creator.id,
            name: ride.creator.name,
            rating: ride.creator.avg_rating,
            picture: ride.creator.profile_picture
          }));
        }
      });

      setStats({
        ridesCreated: created.data.length,
        ridesJoined: joined.data.length,
        ridesCompleted: completed,
        cyclingBuddies: Array.from(buddies).map(b => JSON.parse(b)).slice(0, 5)
      });
    } catch (error) {
      console.error('Failed to fetch stats');
    }
  };

  const fetchRecentRides = async () => {
    try {
      const [created, joined] = await Promise.all([
        axios.get('/rides/my-rides'),
        axios.get('/rides/joined')
      ]);
      
      setRecentRides({
        created: created.data.slice(0, 3),
        joined: joined.data.slice(0, 3)
      });
    } catch (error) {
      console.error('Failed to fetch recent rides');
    }
  };

  const getAchievements = () => {
    const achievements = [];
    if (stats.ridesCompleted >= 1) achievements.push({ icon: '🚴', name: 'First Ride', desc: 'Completed your first ride' });
    if (stats.ridesCompleted >= 5) achievements.push({ icon: '🏆', name: 'Regular Rider', desc: 'Completed 5 rides' });
    if (stats.ridesCompleted >= 10) achievements.push({ icon: '⭐', name: 'Cycling Star', desc: 'Completed 10 rides' });
    if (user.avg_rating >= 4.5) achievements.push({ icon: '💎', name: 'Top Rated', desc: '4.5+ star rating' });
    if (stats.ridesCreated >= 3) achievements.push({ icon: '👑', name: 'Ride Leader', desc: 'Created 3+ rides' });
    return achievements;
  };

  const handleDeleteAccount = async () => {
    try {
      await axios.delete('/users/account');
      toast.success('Account deleted successfully');
      // Logout and redirect
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
      window.location.href = '/auth';
    } catch (error) {
      toast.error('Failed to delete account');
      console.error('Delete account error:', error);
    }
  };

  return (
    <div className="container mx-auto px-4 py-6 max-w-6xl">
      {/* Profile Header */}
      <Card className="mb-6">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row gap-6 items-start md:items-center">
            {/* Profile Picture */}
            <div className="relative">
              <button
                onClick={() => setShowProfileModal(true)}
                className="relative group"
                data-testid="edit-profile-picture"
              >
                {user.profile_picture ? (
                  <img
                    src={user.profile_picture}
                    alt={user.name}
                    className="w-24 h-24 rounded-full object-cover border-4 border-purple-600"
                  />
                ) : (
                  <div className="w-24 h-24 bg-purple-600 rounded-full flex items-center justify-center border-4 border-purple-500">
                    <span className="text-white text-4xl font-bold">
                      {user.name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 rounded-full transition-all flex items-center justify-center">
                  <Camera className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              </button>
            </div>

            {/* Profile Info */}
            <div className="flex-1">
              <h1 className="text-3xl font-bold text-foreground mb-2">{user.name}</h1>
              <p className="text-muted-foreground mb-3">{user.bio || 'No bio yet'}</p>
              <div className="flex flex-wrap gap-2">
                <Badge variant="secondary">{user.experience_level}</Badge>
                <Badge variant="outline">Prefers {user.preferred_distance}km rides</Badge>
              </div>
            </div>

            {/* Rating Display */}
            <div className="text-center bg-accent p-4 rounded-lg">
              <div className="flex items-center justify-center gap-2 mb-1">
                <Star className="w-6 h-6 text-yellow-500" fill="currentColor" />
                <span className="text-3xl font-bold text-foreground">{user.avg_rating.toFixed(1)}</span>
              </div>
              <p className="text-sm text-muted-foreground">{user.total_ratings} ratings</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="p-4 text-center">
            <TrendingUp className="w-8 h-8 text-purple-500 mx-auto mb-2" />
            <p className="text-2xl font-bold text-foreground">{stats.ridesCreated}</p>
            <p className="text-sm text-muted-foreground">Rides Created</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <Users className="w-8 h-8 text-purple-500 mx-auto mb-2" />
            <p className="text-2xl font-bold text-foreground">{stats.ridesJoined}</p>
            <p className="text-sm text-muted-foreground">Rides Joined</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <Calendar className="w-8 h-8 text-purple-500 mx-auto mb-2" />
            <p className="text-2xl font-bold text-foreground">{stats.ridesCompleted}</p>
            <p className="text-sm text-muted-foreground">Completed</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <Award className="w-8 h-8 text-purple-500 mx-auto mb-2" />
            <p className="text-2xl font-bold text-foreground">{getAchievements().length}</p>
            <p className="text-sm text-muted-foreground">Achievements</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Recent Activity */}
        <Card>
          <CardHeader>
            <CardTitle>Recent Rides Created</CardTitle>
          </CardHeader>
          <CardContent>
            {recentRides.created.length === 0 ? (
              <p className="text-muted-foreground text-sm">No rides created yet</p>
            ) : (
              <div className="space-y-3">
                {recentRides.created.map(ride => (
                  <div
                    key={ride.id}
                    className="flex items-center justify-between p-3 bg-accent rounded-lg cursor-pointer hover:bg-accent/80"
                    onClick={() => navigate(`/ride/${ride.id}`)}
                  >
                    <div>
                      <p className="font-medium">{ride.distance}km</p>
                      <p className="text-sm text-muted-foreground">{ride.date}</p>
                    </div>
                    <Badge variant={ride.status === 'completed' ? 'default' : 'secondary'}>
                      {ride.status}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Recent Rides Joined</CardTitle>
          </CardHeader>
          <CardContent>
            {recentRides.joined.length === 0 ? (
              <p className="text-muted-foreground text-sm">No rides joined yet</p>
            ) : (
              <div className="space-y-3">
                {recentRides.joined.map(ride => (
                  <div
                    key={ride.id}
                    className="flex items-center justify-between p-3 bg-accent rounded-lg cursor-pointer hover:bg-accent/80"
                    onClick={() => navigate(`/ride/${ride.id}`)}
                  >
                    <div>
                      <p className="font-medium">{ride.distance}km with {ride.creator?.name}</p>
                      <p className="text-sm text-muted-foreground">{ride.date}</p>
                    </div>
                    <Badge variant={ride.status === 'completed' ? 'default' : 'secondary'}>
                      {ride.status}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Achievements */}
        <Card>
          <CardHeader>
            <CardTitle>Achievements</CardTitle>
          </CardHeader>
          <CardContent>
            {getAchievements().length === 0 ? (
              <p className="text-muted-foreground text-sm">Complete rides to earn achievements!</p>
            ) : (
              <div className="space-y-3">
                {getAchievements().map((achievement, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-3 bg-accent rounded-lg">
                    <span className="text-3xl">{achievement.icon}</span>
                    <div>
                      <p className="font-medium">{achievement.name}</p>
                      <p className="text-sm text-muted-foreground">{achievement.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Cycling Buddies */}
        <Card>
          <CardHeader>
            <CardTitle>Cycling Buddies</CardTitle>
          </CardHeader>
          <CardContent>
            {stats.cyclingBuddies.length === 0 ? (
              <p className="text-muted-foreground text-sm">Join rides to meet cycling buddies!</p>
            ) : (
              <div className="space-y-3">
                {stats.cyclingBuddies.map(buddy => (
                  <div key={buddy.id} className="flex items-center gap-3 p-3 bg-accent rounded-lg">
                    {buddy.picture ? (
                      <img src={buddy.picture} alt={buddy.name} className="w-10 h-10 rounded-full object-cover" />
                    ) : (
                      <div className="w-10 h-10 bg-purple-600 rounded-full flex items-center justify-center">
                        <span className="text-white font-bold">{buddy.name.charAt(0)}</span>
                      </div>
                    )}
                    <div className="flex-1">
                      <p className="font-medium">{buddy.name}</p>
                      <div className="flex items-center gap-1">
                        <Star className="w-3 h-3 text-yellow-500" fill="currentColor" />
                        <span className="text-sm text-muted-foreground">{buddy.rating.toFixed(1)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Account Settings - Subtle */}
      <div className="mt-6 text-center">
        <button
          onClick={() => setShowSettingsModal(true)}
          className="text-sm text-muted-foreground hover:text-purple-500 transition-colors underline"
        >
          Account Settings
        </button>
      </div>

      {/* Profile Picture Modal */}
      {showProfileModal && (
        <ProfilePictureModal
          user={user}
          onClose={() => setShowProfileModal(false)}
          onUpdated={onUpdate}
        />
      )}

      {/* Account Settings Modal */}
      {showSettingsModal && (
        <AccountSettingsModal
          user={user}
          onClose={() => setShowSettingsModal(false)}
          onUpdate={onUpdate}
        />
      )}
    </div>
  );
}
